/**
 * This custom hook provides the functionality to save a user with the given parameters.
 */

import {useHistory} from "react-router-dom";
import {useSnackbar} from "notistack";
import {firebaseService} from "../../firebase/Firebase";
import {useContext, useState} from "react";
import {FirebaseAuthContext} from "../../firebase/FirebaseAuthProvider";
import {useIntl} from "react-intl";

const useSelfRegistration = () => {
    const history = useHistory();
    const {clearUserData} = useContext(FirebaseAuthContext)
    const {enqueueSnackbar} = useSnackbar();
    const [registrationInProgress, setRegistrationInProgress] = useState(false)

    // Internationalization for simple strings
    const intl = useIntl();

    const registerUser = async (user) => {
        setRegistrationInProgress(true)
        // Request the user registration
        fetch('/reg-main/profiles', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                userData: user
            })
        })
            .then(async (data) => {
                // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
                if (data.status !== 200) {
                    throw new Error((await data.json()).status)
                }

                // If the registration was successful and also the user data is saved in the DB - automatically sign-in the user
                return firebaseService.auth().signInWithEmailAndPassword(user.email, user.password)
            })
            .then((userCredential) => {
                return firebaseService.auth().currentUser.sendEmailVerification({
                    url: process.env.REACT_APP_OWN_URL
                })
            })
            .then(() => {
                enqueueSnackbar(intl.formatMessage({
                    id: 'app.signUp.messages.verificationLinkSend.success',
                    defaultMessage: 'A verification link has been sent to your email address - Please confirm it!'
                }), {
                    variant: 'success',
                    preventDuplicate: true,
                    autoHideDuration: 3000
                });
                firebaseService.auth().currentUser.getIdToken(true);
                history.push("/waitingForEmailVerification");
            })
            .catch((error) => {
                enqueueSnackbar(intl.formatMessage({
                    id: 'app.signUp.messages.accountCreation.error',
                    defaultMessage: 'Something went wrong while creating the user: {error}'
                }, {error: error.message}), {
                    variant: 'error',
                    preventDuplicate: true,
                    autoHideDuration: 3000
                });
            })
            .finally(() => {
                setRegistrationInProgress(false)
            })
    }

    const resendVerificationEmail = () => {
        firebaseService.auth().currentUser.sendEmailVerification({
            url: process.env.REACT_APP_OWN_URL
        })
            .then(() => {
                enqueueSnackbar(intl.formatMessage({
                    id: 'app.signUp.messages.verificationLinkResend.success',
                    defaultMessage: 'The verification link has been resent to your email address - Please confirm it!'
                }), {
                    variant: 'success',
                    preventDuplicate: true,
                    autoHideDuration: 3000
                });
            })
            .catch((error) => {
                enqueueSnackbar(intl.formatMessage({
                    id: 'app.signUp.messages.verificationLinkResend.error',
                    defaultMessage: 'Something went wrong while re-sending the email verification link: {error}'
                }, {error: error.message}), {
                    variant: 'error',
                    preventDuplicate: true,
                    autoHideDuration: 3000
                });
            })
    }

    const backToSignIn = () => {
        clearUserData()
        firebaseService.auth().signOut()
    }

    return {registerUser, resendVerificationEmail, backToSignIn, registrationInProgress}
}
export default useSelfRegistration;